import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEntityList, createEntity, updateEntity, deleteEntity } from '../data/entities';
import { useToast } from '../components/Toast';
import SequenceEditor from './SequenceEditor';
import {
  AUDIENCES,
  SKILLS,
  STYLE_OPTIONS,
  filterLessons,
  lessonStats,
  styleLabel,
  syncSamplePack,
  normalizeStyle,
} from './lessonCatalog';
import { sequenceToCues, normalizeSequence, sequenceDurationMins } from './sequenceModel';
import { useUserProfile } from './useUserProfile';
import { canManageLessons, planLabel } from './planTypes';

const EMPTY = {
  title: '',
  description: '',
  audience: 'adults',
  skillLevel: 'beginner',
  style: 'hatha',
  props: '',
  karmaPack: false,
  sequence: [],
};

function ClassList({ lessons, planType, onEdit, onPreview, onDelete, busy }) {
  const list = filterLessons(lessons, { planType }) || [];
  if (!list.length) {
    return (
      <div className="cs-empty">
        No classes yet. Create one, or refresh the sample pack.
      </div>
    );
  }
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      {list.map((lesson) => {
        const { poseCount, durationMins } = lessonStats(lesson, planType);
        return (
          <article key={lesson.id} className="yoga-teach-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: 'var(--fs-md)' }}>{lesson.title}</h3>
              <p className="u-muted u-small" style={{ margin: 'var(--sp-1) 0 0' }}>
                {styleLabel(lesson.style)} · {lesson.skillLevel} · {durationMins} min · {poseCount} poses
                {lesson.karmaPack ? ' · Karma pack' : ''}
              </p>
            </div>
            <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
              <button type="button" className="cs-btn cs-btn-sm" onClick={() => onPreview(lesson)} disabled={busy}>
                Preview
              </button>
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onEdit(lesson)} disabled={busy}>
                Edit
              </button>
              <button
                type="button"
                className="cs-btn cs-btn-ghost cs-btn-sm"
                disabled={busy}
                onClick={() => onDelete(lesson)}
              >
                Delete
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function Editor({ initial, onCancel, onSaved }) {
  const toast = useToast();
  const [draft, setDraft] = useState(() => ({
    ...EMPTY,
    ...initial,
    sequence: normalizeSequence(initial?.sequence || [], { style: initial?.style || 'hatha' }),
  }));
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const mins = sequenceDurationMins(draft.sequence);

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    const title = draft.title.trim();
    if (!title) {
      toast('Title is required', 'danger');
      return;
    }
    const sequence = normalizeSequence(draft.sequence, { style: draft.style });
    if (!sequence.length) {
      toast('Add at least one pose to the sequence', 'danger');
      return;
    }
    setBusy(true);
    try {
      const cues = sequenceToCues(sequence, { teacher: false });
      const cuesTeacher = sequenceToCues(sequence, { teacher: true });
      const payload = {
        title,
        description: String(draft.description || '').trim(),
        audience: draft.audience,
        skillLevel: draft.skillLevel,
        style: normalizeStyle(draft.style) || 'hatha',
        props: String(draft.props || '').trim(),
        karmaPack: !!draft.karmaPack,
        sequence,
        cues,
        cuesTeacher,
        durationMins: mins || Number(draft.durationMins) || 30,
      };
      if (isEdit) {
        await updateEntity('yogaLesson', {}, initial.id, payload);
        toast('Class updated', 'ok');
      } else {
        await createEntity('yogaLesson', {}, payload);
        toast('Class created', 'ok');
      }
      onSaved();
    } catch (err) {
      toast(`Could not save: ${err.message}`, 'danger', 8000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="yoga-teach-editor u-stack m-pop" style={{ '--gap': 'var(--sp-4)' }} onSubmit={save}>
      <div className="u-split" style={{ alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>
          {isEdit ? 'Edit class' : 'New class'}
        </h2>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>

      <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
        <span className="u-xs u-muted">Title</span>
        <input
          className="cs-input"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          disabled={busy}
          required
        />
      </label>

      <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
        <span className="u-xs u-muted">Description</span>
        <textarea
          className="cs-input"
          rows={2}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          disabled={busy}
          style={{ resize: 'vertical' }}
        />
      </label>

      <div className="yoga-teach-meta">
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <span className="u-xs u-muted">Style</span>
          <select
            className="cs-input"
            value={draft.style}
            onChange={(e) => setDraft({ ...draft, style: e.target.value })}
            disabled={busy}
          >
            {STYLE_OPTIONS.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </label>
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <span className="u-xs u-muted">Audience</span>
          <select
            className="cs-input"
            value={draft.audience}
            onChange={(e) => setDraft({ ...draft, audience: e.target.value })}
            disabled={busy}
          >
            {AUDIENCES.filter((a) => a.id !== 'all').map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </label>
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <span className="u-xs u-muted">Skill</span>
          <select
            className="cs-input"
            value={draft.skillLevel}
            onChange={(e) => setDraft({ ...draft, skillLevel: e.target.value })}
            disabled={busy}
          >
            {SKILLS.filter((s) => s.id !== 'all').map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
        <span className="u-xs u-muted">Props</span>
        <input
          className="cs-input"
          value={draft.props}
          onChange={(e) => setDraft({ ...draft, props: e.target.value })}
          disabled={busy}
          placeholder="Mat, blocks, strap…"
        />
      </label>

      <label className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={!!draft.karmaPack}
          onChange={(e) => setDraft({ ...draft, karmaPack: e.target.checked })}
          disabled={busy}
        />
        <span className="u-small">Include in Karma Yogi pack</span>
      </label>

      <SequenceEditor
        steps={draft.sequence}
        style={draft.style}
        onChange={(sequence) => setDraft({ ...draft, sequence })}
      />

      <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
        <button className="cs-btn primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create class'}
        </button>
        <span className="u-muted u-small">Live duration ~{mins} min</span>
      </div>
    </form>
  );
}

function TeacherWorkspace() {
  const toast = useToast();
  const navigate = useNavigate();
  const { uid, planType } = useUserProfile();
  const { docs: lessons, error } = useEntityList('yogaLesson', {});
  const [editing, setEditing] = useState(null); // null | 'new' | lesson
  const [busy, setBusy] = useState(false);
  const autoSyncRan = useRef(false);

  useEffect(() => {
    if (!uid || !planType || lessons == null) return;
    if (autoSyncRan.current) return;
    autoSyncRan.current = true;
    (async () => {
      try {
        await syncSamplePack(lessons, { force: false });
      } catch (err) {
        toast(`Could not refresh samples: ${err.message}`, 'danger', 6000);
      }
    })();
  }, [uid, planType, lessons, toast]);

  const loadSamples = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { created, updated } = await syncSamplePack(lessons, { force: true });
      toast(
        created || updated
          ? `Samples ready (${created} new, ${updated} updated)`
          : 'Samples already up to date',
        'ok',
      );
    } catch (err) {
      toast(`Could not load samples: ${err.message}`, 'danger', 8000);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (lesson) => {
    if (busy) return;
    if (!window.confirm(`Delete “${lesson.title}”?`)) return;
    setBusy(true);
    try {
      await deleteEntity('yogaLesson', {}, lesson.id);
      toast('Class deleted', 'ok');
      if (editing?.id === lesson.id) setEditing(null);
    } catch (err) {
      toast(`Could not delete: ${err.message}`, 'danger', 8000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }}>
      {editing ? (
        <Editor
          initial={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      ) : (
        <>
          <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
            <button type="button" className="cs-btn primary" onClick={() => setEditing('new')}>
              New class
            </button>
            <button type="button" className="cs-btn cs-btn-outline" disabled={busy} onClick={loadSamples}>
              {busy ? 'Working…' : 'Refresh sample pack'}
            </button>
          </div>
          {error && (
            <div className="cs-banner b-danger" role="alert">
              Could not load classes: {error}
            </div>
          )}
          {lessons == null ? (
            <p className="cs-help">
              <span className="cs-spinner" aria-hidden /> Loading…
            </p>
          ) : (
            <ClassList
              lessons={lessons}
              planType={planType}
              busy={busy}
              onEdit={(l) => setEditing(l)}
              onPreview={(l) => navigate(`/practice/${l.id}`)}
              onDelete={onDelete}
            />
          )}
        </>
      )}
    </div>
  );
}

function LockedTeach() {
  return (
    <div className="yoga-teach-locked u-stack m-rise" style={{ '--gap': 'var(--sp-4)', maxWidth: 420 }}>
      <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>Teach is for Yoga Teachers</h2>
      <p className="u-muted" style={{ margin: 0, lineHeight: 1.55 }}>
        Build classes as timed pose sequences — search Pocket Yoga poses, set holds and cues, preview in the practice player.
      </p>
      <Link className="cs-btn primary" to="/membership">
        View Membership
      </Link>
      <p className="u-muted u-small" style={{ margin: 0 }}>
        Preview only — switching to Yoga Teacher is free in this build (no billing).
      </p>
    </div>
  );
}

export default function Teach() {
  const { planType, loading } = useUserProfile();
  const manage = canManageLessons(planType);

  return (
    <div className="cs-page yoga-shell u-stack m-fade-in" style={{ '--gap': 'var(--sp-6)' }}>
      <header className="yoga-page-head m-rise">
        <p className="yoga-eyebrow">YogaKai</p>
        <h1 className="yoga-hero-title">Teach</h1>
        <p className="yoga-hero-lead">
          {manage
            ? 'Create and edit classes as pose sequences. Preview opens the timer player.'
            : 'Sequence builder for leading class — unlock with the Yoga Teacher plan.'}
        </p>
        {planType ? (
          <p className="u-muted u-small" style={{ margin: 0 }}>
            Plan: <strong>{planLabel(planType)}</strong>
          </p>
        ) : null}
      </header>

      {loading ? (
        <p className="cs-help">
          <span className="cs-spinner" aria-hidden /> Loading…
        </p>
      ) : manage ? (
        <TeacherWorkspace />
      ) : (
        <LockedTeach />
      )}
    </div>
  );
}
